import * as React from 'react';
import {FormattedMessage} from "react-intl";

import {
    useResidenceExplorerStore,
    useSunStudyPeriodNames,
    selectCurrentSunStudy,
    selectSetup,
    selectSunStudyHasMultiplePeriods,
    selectSunStudyPeriod,
    selectSunStudyStarted,
} from "features/residenceExplorer/store/useResidenceExplorerStore";
import {useDeviceStore, selectIsMobile} from "features/ui/store/useDeviceStore";
import {getFormattedMessage} from "features/services/utils/language-helper";

import './_residenceExplorerSunStudyControls.scss';

import {P} from "@atoms/Text/P";
import {Icon} from "@atoms/Media/Icon";
import {Button, ButtonColor, ButtonSize, ButtonState, ButtonType} from "@atoms/LinksAndButtons/Button";
import {UserAgent} from "@atoms/UserAgent";
import {
    ResidenceExplorerSunStudySlider
} from "features/residenceExplorer/components/ResidenceExplorerV2/_residenceExplorerSunStudySlider";


const months = [
    getFormattedMessage('jm.components.SunStudyControlls.jan'),
    getFormattedMessage('jm.components.SunStudyControlls.feb'),
    getFormattedMessage('jm.components.SunStudyControlls.mars'),
    getFormattedMessage('jm.components.SunStudyControlls.april'),
    getFormattedMessage('jm.components.SunStudyControlls.maj'),
    getFormattedMessage('jm.components.SunStudyControlls.juni'),
    getFormattedMessage('jm.components.SunStudyControlls.juli'),
    getFormattedMessage('jm.components.SunStudyControlls.aug'),
    getFormattedMessage('jm.components.SunStudyControlls.sep'),
    getFormattedMessage('jm.components.SunStudyControlls.oct'),
    getFormattedMessage('jm.components.SunStudyControlls.nov'),
    getFormattedMessage('jm.components.SunStudyControlls.dec')
];

export const ResidenceExplorerSunStudyControls: React.FunctionComponent = () => {
    const setup = useResidenceExplorerStore(selectSetup);
    const _isMobile = useDeviceStore(selectIsMobile);
    const currentSunStudy = useResidenceExplorerStore(selectCurrentSunStudy);
    const sunStudyPlaying = useResidenceExplorerStore(selectSunStudyStarted);
    const hasMultiplePeriods = useResidenceExplorerStore(selectSunStudyHasMultiplePeriods);
    const periodNames = useSunStudyPeriodNames();
    const selectedPeriod = useResidenceExplorerStore(selectSunStudyPeriod);
    const setShowSunStudy = useResidenceExplorerStore(state => state.setShowSunStudy);
    const setSunStudyPeriod = useResidenceExplorerStore(state => state.setSunStudyPeriod);
    const setSunStudyStarted = useResidenceExplorerStore(state => state.setSunStudyStarted);

    if(!setup || !currentSunStudy) return null;

    const getCorrectMonthName = (month: string) => {
        const matchName = new RegExp(/\d{1,2}:[A-Za-z] ([A-Za-z].*)/);
        const matches = month.match(matchName);

        if(matches !== null && typeof matches[1] !== 'undefined') {
            const regMonth = matches[1];
            const result = months.map(m => m.startsWith(regMonth)).indexOf(true);

            if(result > -1) {
                return month.replace(regMonth, months[result]);
            }
            return month;
        }
        return month;
    };

    const closeSunStudy = () => {
        setShowSunStudy(false);
    };

    const toggleView = (index: number) => () =>{
        setSunStudyPeriod(index);
    };

    const play = (_time: number) => () => {
        setSunStudyStarted(true);
    };

    const stop = () => () => {
        setSunStudyStarted(false);
    };

    return (
        <div className="sun-study-controlls sun-study-controlls--active">
            <React.Fragment>
                <div className="sun-study-controlls__top">
                    {hasMultiplePeriods &&
                        <P>
                            <FormattedMessage
                                id="jm.components.SunStudyControlls.chooseSeason"
                                values={{projectName: setup.stageName}}
                            />
                        </P>
                    }
                    <div className="sun-study-controlls__close" onClick={closeSunStudy}>
                        <UserAgent computer>
                            <span><FormattedMessage id="jm.components.SunStudyControlls.closeSunStudy" /> </span>
                        </UserAgent>
                        <Icon name="x" />
                    </div>
                </div>

                <div className="sun-study-controlls__bottom">
                    {hasMultiplePeriods &&
                        <div className="sun-study-controlls__toggle-buttons toggle-buttons toggle-buttons--switch">
                            {periodNames && periodNames.length > 1 && periodNames.map((period, index) => (
                                <Button
                                    key={index}
                                    onClick={toggleView(index)}
                                    buttonState={index === selectedPeriod
                                        ? ButtonState.ACTIVE
                                        : ButtonState.DEFAULT
                                    }
                                    buttonColor={ButtonColor.DEFAULT}
                                    buttonSize={ButtonSize.DEFAULT}
                                    buttonType={ButtonType.TOGGLE}
                                >
                                    {getCorrectMonthName(period)}
                                </Button>
                            ))}
                        </div>
                    }

                    {(!hasMultiplePeriods && currentSunStudy.name) &&
                        <div className="sun-study-controlls__toggle-buttons toggle-buttons toggle-buttons--switch">
                            <Button
                                onClick={() => {}}
                                className="button-wide"
                                buttonColor={ButtonColor.DEFAULT}
                                buttonSize={ButtonSize.DEFAULT}
                                buttonType={ButtonType.TOGGLE}
                                buttonState={ButtonState.INACTIVE}
                            >
                                {getCorrectMonthName(currentSunStudy.name)}
                            </Button>
                        </div>
                    }

                    <div className="sun-study-controlls__slider">
                        <ResidenceExplorerSunStudySlider />
                        {/*
                            <SunStudySlider
                                snapPoint={imageCount}
                                frame={currentFrame}
                                onChange={goToFrame}
                                date={winterActive ?
                                    getCorrectMonthName(buttonLabels[1]) :
                                    getCorrectMonthName(buttonLabels[0])}
                                wec360={imageCount === 3}
                                startHour={startHour}
                                endHour={endHour}
                                imageCount={imageCount}
                            />
                        */}
                    </div>

                    {currentSunStudy.imageCount !== 3 &&
                        <div
                            className="sun-study-controlls__play"
                            onClick={!sunStudyPlaying ?
                                play(250) :
                                stop()}
                        >
                            <Icon name="sun-study-control"/>
                            {!sunStudyPlaying &&
                                <Icon name="play"/>
                            }
                            {sunStudyPlaying &&
                                <Icon name="pause"/>
                            }
                        </div>
                    }
                </div>
            </React.Fragment>
        </div>
    )
};
